import { v4 as uuidv4 } from 'uuid';
import type { FulfillmentMode, OrderItem, PosComboSearchItem, PosVariant, PosVariantDetailItem } from '../types';
import { rebalanceFlashGroup, recalcItem, variantToOrderItem } from './orderItemCalc';
import { applyTierDiscount } from './suggestPosB2BPrices';
import { addComboToCart } from './addComboToCart';

/**
 * TCR-2303 — "Thêm nhanh hàng hóa" cho màn lên đơn B2B (MT/GT).
 *
 * File này chứa TOÀN BỘ logic thuần (parse danh sách dán, dựng dòng với giá/CK/VAT NVBH sửa,
 * gộp vào giỏ) để unit test được, còn popup (`QuickAddItemsModal`) chỉ lo giao diện.
 *
 * NGUYÊN TẮC CỐT LÕI — không tự chế công thức giá thứ hai:
 *  - Dòng mặc định (`defaults`) do popup dựng bằng ĐÚNG đường của giỏ: `variantToOrderItem`
 *    rồi `applyB2BSuggestToOrderItems` (cùng hàm effect gợi ý giá ở `POS/index.tsx` gọi). Nhờ đó
 *    giá/CK mặc định trong popup TRÙNG với giá bảng hàng sẽ hiện sau khi thêm.
 *  - Phần NVBH sửa chỉ là lớp `edits` phủ lên `defaults`; khi vào giỏ, mỗi giá trị sửa mang cờ
 *    bảo vệ riêng (`hasPriceOverride` / `hasDiscountOverride` / `hasVatOverride`) — chính các cờ
 *    mà `OrderItemsTable` đặt khi NVBH sửa tay và effect gợi ý giá B2B tôn trọng. Không sửa thì
 *    KHÔNG đặt cờ, để gợi ý giá áp bình thường (đổi SL nhảy cấp giá vẫn đúng).
 */

// ─────────────────────────────── 1. Parse danh sách dán ───────────────────────────────

export interface QuickAddParsedEntry {
  /** Mã đúng như người dùng gõ (giữ hoa/thường của lần xuất hiện đầu tiên). */
  code: string;
  quantity: number;
}

export interface QuickAddParseResult {
  entries: QuickAddParsedEntry[];
  /** Dòng có SL không hợp lệ (chữ, số âm, 0, số lẻ) — báo lỗi, không đoán. */
  invalidLines: string[];
}

/**
 * Mỗi dòng: `mã` + `SL`, cách nhau bằng tab / dấu phẩy / chấm phẩy / khoảng trắng (dán thẳng từ
 * Excel là tab). Thiếu SL → 1. Mã trùng trong danh sách → CỘNG DỒN SL (so khớp không phân biệt
 * hoa/thường) và giữ thứ tự xuất hiện lần đầu.
 *
 * SL có mặt nhưng không phải số nguyên dương → đưa vào `invalidLines` thay vì lặng lẽ về 1:
 * "A abc" rất có thể là gõ nhầm, tự đổi thành 1 sẽ làm sai đơn mà không ai hay.
 */
export function parseQuickAddList(text: string): QuickAddParseResult {
  const order: string[] = [];
  const byKey = new Map<string, QuickAddParsedEntry>();
  const invalidLines: string[] = [];

  for (const rawLine of text.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    const [code, qtyToken] = line.split(/[\t,;\s]+/).filter(Boolean);
    if (!code) continue;

    let quantity = 1;
    if (qtyToken !== undefined) {
      // Excel vi-VN có thể xuất "1.000" (phân cách nghìn) — bỏ dấu chấm trước khi đọc.
      const n = Number(qtyToken.replace(/\./g, ''));
      if (!Number.isInteger(n) || n <= 0) {
        invalidLines.push(line);
        continue;
      }
      quantity = n;
    }

    const key = code.toLowerCase();
    const existing = byKey.get(key);
    if (existing) {
      existing.quantity += quantity;
    } else {
      const entry = { code, quantity };
      byKey.set(key, entry);
      order.push(key);
    }
  }

  return { entries: order.map((k) => byKey.get(k)!), invalidLines };
}

// ─────────────────────────────── 2. Khóa sửa giá + dòng hiệu lực ───────────────────────────────

/** Giá trị NVBH sửa trong popup. Thiếu key = không sửa (dùng mặc định). */
export interface QuickAddEdits {
  /** Đơn giá CHƯA VAT — cùng đơn vị với `OrderItem.price`. */
  price?: number;
  /** CK theo %. */
  discountPercent?: number;
  /** VAT %. */
  vat?: number;
}

export interface QuickAddEntry {
  variant: PosVariant;
  quantity: number;
  /** Dòng mặc định (đã qua gợi ý giá B2B) — nguồn của mọi giá trị chưa sửa. */
  defaults: OrderItem;
  edits: QuickAddEdits;
}

/**
 * TCR-2407 — một combo đã tick từ lưới "Chọn từ lưới sản phẩm".
 *
 * Combo ở lưới là ComboDefinition loại VIRTUAL, KHÔNG phải variant, nên
 * KHÔNG thể tra `combo_sku` qua `lookupVariants` như mã dán. Đúng đường: tra
 * TỪNG THÀNH PHẦN (`component_items[].variant_code`) lúc tick, giữ chi tiết
 * trong pick này; lúc xác nhận cha gọi `appendComboPicksToCart` (dùng
 * `addComboToCart` — cùng hàm màn chính) để vào giỏ thành cụm dòng chung
 * `comboGroupId`.
 */
export interface QuickAddComboPick {
  combo: PosComboSearchItem;
  /** Chi tiết tươi của các thành phần (tra cùng lúc tick) — nguồn dựng cụm dòng. */
  details: PosVariantDetailItem[];
  /** Số combo; mỗi combo tạo 1 cụm riêng (cùng quy ước "thêm N lần" của màn chính). */
  quantity: number;
}

export type QuickAddLock = 'flash' | 'combo' | null;

/**
 * Hàng chỉ cho sửa SL (ô giá + CK khóa):
 *  - flash sale: giá là giá CHƯƠNG TRÌNH, sửa tay làm lệch engine khuyến mãi (xem ghi chú
 *    `variantToOrderItem` về `hasPriceOverride`);
 *  - combo thật (`is_combo_product`): giá đã chốt theo định mức.
 */
export function getQuickAddLock(variant: PosVariant): QuickAddLock {
  if (variant.flash_sale) return 'flash';
  if (variant.is_combo_product) return 'combo';
  return null;
}

/** Hàng serial không nhập theo lô được (mỗi dòng là 1 serial cụ thể) — chỉ khi giao ngay. */
export function isQuickAddSerialRejected(variant: PosVariant, mode: FulfillmentMode): boolean {
  return variant.tracking_mode === 'serial' && mode === 'INSTANT';
}

/** Chênh lệch dưới 1 xu coi như KHÔNG sửa — cùng ngưỡng `OrderItemsTable.handlePriceBlur`. */
const PRICE_EPSILON = 0.01;

/** Giá trị sửa có thực sự khác mặc định không (gõ lại đúng giá cũ không phải "sửa tay"). */
export function isPriceEdited(defaults: OrderItem, edits: QuickAddEdits): boolean {
  return edits.price !== undefined && Math.abs(edits.price - defaults.price) >= PRICE_EPSILON;
}
export function isDiscountEdited(defaults: OrderItem, edits: QuickAddEdits): boolean {
  return (
    edits.discountPercent !== undefined &&
    Math.abs(edits.discountPercent - defaults.discountPercent) >= PRICE_EPSILON
  );
}
export function isVatEdited(defaults: OrderItem, edits: QuickAddEdits): boolean {
  return edits.vat !== undefined && edits.vat !== (defaults.vat ?? 0);
}

/**
 * Dòng HIỆU LỰC = mặc định + SL + phần sửa. Dùng cho CẢ xem trước lẫn dòng mới đưa vào giỏ, nên
 * số trong popup luôn bằng số trong giỏ.
 *
 * Thứ tự quan trọng: đặt cờ → `applyTierDiscount`. Hàm đó là đường tính CK của bảng hàng khi
 * NVBH sửa giá: CK người dùng đã sửa (`hasDiscountOverride`) được giữ nguyên, còn CK cấp giá
 * (kể cả dạng ₫/SP) được tính lại trên giá/SL mới.
 */
export function applyQuickAddEdits(
  defaults: OrderItem,
  quantity: number,
  edits: QuickAddEdits,
  fulfillmentMode: FulfillmentMode,
  lock: QuickAddLock = null,
): OrderItem {
  let item: OrderItem = { ...defaults, quantity };

  if (!lock) {
    if (isPriceEdited(defaults, edits)) {
      item = { ...item, price: edits.price!, hasPriceOverride: true };
    }
    if (isDiscountEdited(defaults, edits)) {
      item = {
        ...item,
        discountType: 'percent',
        discountPercent: Math.min(100, Math.max(0, edits.discountPercent!)),
        hasDiscountOverride: true,
      };
    }
  }
  // VAT không bị khóa theo hàng (bảng hàng cũng không khóa theo quyền) — NVBH sửa được VAT cả
  // với hàng flash/combo.
  if (isVatEdited(defaults, edits)) {
    item = { ...item, vat: edits.vat!, vatOverride: edits.vat!, hasVatOverride: true };
  }
  return applyTierDiscount(item, fulfillmentMode);
}

// ─────────────────────────────── 3. Thông tin giỏ hiện tại ───────────────────────────────

/**
 * Dòng LẺ đầu tiên của biến thể mà `addItem` (POS/index.tsx) sẽ cộng dồn vào: loại dòng thuộc
 * cụm combo ảo (phá bất biến `quantity == comboUnitQty × count`), dòng flash sale và quà tặng
 * (SL/giá do khuyến mãi khóa). Cũng loại dòng dư flash (`flashOverflowOf`) — nó là nửa nhóm
 * flash, SL do `rebalanceFlashGroup` quản.
 */
export function findMergeTargetIndex(items: OrderItem[], variantId: string): number {
  return items.findIndex(
    (i) =>
      i.variantId === variantId &&
      !i.comboGroupId &&
      !i.flash_sale_id &&
      !i.flash_sale_item_id &&
      !i.flashOverflowOf &&
      !i.is_gift &&
      // TCR-2614: dòng tách (hàng tặng giá 0) không nhận SL cộng dồn.
      !i.splitLine &&
      // Dòng giá 0 đã lưu (mất cờ splitLine khi mở lại đơn) cũng không nhận SL cộng dồn.
      i.price !== 0,
  );
}

export interface QuickAddCartInfo {
  /** Tổng SL của biến thể trên MỌI dòng giỏ (kể cả combo/quà — chúng cùng trừ tồn). */
  totalQty: number;
  /** SL của dòng lẻ sẽ được cộng dồn; `undefined` nếu sẽ tạo dòng mới. */
  mergeTargetQty?: number;
}

export function getQuickAddCartInfo(items: OrderItem[], variantId: string): QuickAddCartInfo {
  const totalQty = items
    .filter((i) => i.variantId === variantId)
    .reduce((sum, i) => sum + i.quantity, 0);
  const idx = findMergeTargetIndex(items, variantId);
  return { totalQty, mergeTargetQty: idx > -1 ? items[idx].quantity : undefined };
}

// ─────────────────────────────── 4. Gộp lô vào giỏ ───────────────────────────────

export interface QuickAddResult {
  items: OrderItem[];
  /** Số mã tạo dòng MỚI. */
  addedCount: number;
  /** Số mã cộng dồn vào dòng có sẵn. */
  mergedCount: number;
  /** Mã bị bỏ qua (serial không nhập lô được) — popup đã chặn từ trước, đây là lớp phòng thủ. */
  skippedCodes: string[];
}

/**
 * Đưa cả lô vào giỏ trong MỘT lần tính (popup gọi `setOrderItems` đúng 1 lần) — gọi `addItem`
 * N lần rồi `updateItem` N lần dễ lệch id / ghi đè nhau giữa các lần ghi cùng tick.
 *
 * Với mỗi mục:
 *  - Hàng flash: không bao giờ cộng thẳng vào dòng thường. Có nhóm flash sẵn thì dồn SL vào nhóm,
 *    chưa có thì tạo dòng flash; cả hai đều cho `rebalanceFlashGroup` chia lại suất KM (phần vượt
 *    suất về giá gốc) — cùng cơ chế bảng hàng dùng khi đổi SL dòng flash.
 *  - Còn lại: cộng dồn vào dòng lẻ đầu tiên (`findMergeTargetIndex`, đúng quy tắc `addItem`);
 *    không có thì thêm dòng mới dựng từ `applyQuickAddEdits`.
 *
 * Khi cộng dồn vào dòng CÓ SẴN, chỉ giá trị NVBH THỰC SỰ sửa trong popup mới ghi đè dòng đó (kèm
 * cờ bảo vệ); giá trị không sửa giữ nguyên của dòng cũ — NVBH thêm 5 cái nữa không có nghĩa là
 * muốn xóa giá thỏa thuận đã nhập trước đó.
 */
export function mergeQuickAddIntoCart(
  prev: OrderItem[],
  entries: QuickAddEntry[],
  fulfillmentMode: FulfillmentMode,
  newId: () => string = uuidv4,
): QuickAddResult {
  let items = prev;
  let addedCount = 0;
  let mergedCount = 0;
  const skippedCodes: string[] = [];

  for (const entry of entries) {
    const { variant, quantity, defaults, edits } = entry;
    if (quantity <= 0) continue;
    if (isQuickAddSerialRejected(variant, fulfillmentMode)) {
      skippedCodes.push(variant.variant_code);
      continue;
    }
    const lock = getQuickAddLock(variant);

    // ── Hàng flash sale ──
    if (lock === 'flash') {
      const groupKey = defaults.flash_sale_item_id;
      const existing = groupKey
        ? items.find((i) => i.flash_sale_item_id === groupKey || i.flashOverflowOf === groupKey)
        : undefined;
      if (existing) {
        const rebalanced = rebalanceFlashGroup(
          items,
          existing.id,
          existing.quantity + quantity,
          fulfillmentMode,
          newId,
        );
        items = rebalanced ?? items.map((i) =>
          i.id === existing.id ? recalcItem({ ...i, quantity: i.quantity + quantity }, fulfillmentMode) : i,
        );
        mergedCount += 1;
        continue;
      }
      const line = applyQuickAddEdits(
        { ...defaults, id: newId() },
        quantity,
        edits,
        fulfillmentMode,
        lock,
      );
      const withLine = [...items, line];
      items = rebalanceFlashGroup(withLine, line.id, quantity, fulfillmentMode, newId) ?? withLine;
      addedCount += 1;
      continue;
    }

    // ── Hàng thường / combo thật ──
    const targetIdx = findMergeTargetIndex(items, variant.variant_id);
    if (targetIdx > -1) {
      const target = items[targetIdx];
      let merged: OrderItem = { ...target, quantity: target.quantity + quantity };
      if (!lock) {
        if (isPriceEdited(defaults, edits)) {
          merged = { ...merged, price: edits.price!, hasPriceOverride: true };
        }
        if (isDiscountEdited(defaults, edits)) {
          merged = {
            ...merged,
            discountType: 'percent',
            discountPercent: Math.min(100, Math.max(0, edits.discountPercent!)),
            hasDiscountOverride: true,
          };
        }
      }
      if (isVatEdited(defaults, edits)) {
        merged = { ...merged, vat: edits.vat!, vatOverride: edits.vat!, hasVatOverride: true };
      }
      const next = [...items];
      next[targetIdx] = applyTierDiscount(merged, fulfillmentMode);
      items = next;
      mergedCount += 1;
      continue;
    }

    items = [
      ...items,
      applyQuickAddEdits({ ...defaults, id: newId() }, quantity, edits, fulfillmentMode, lock),
    ];
    addedCount += 1;
  }

  return { items, addedCount, mergedCount, skippedCodes };
}

/**
 * TCR-2407 — đưa các combo đã tick từ lưới vào giỏ ĐÚNG đường của màn chính:
 * mỗi pick → `addComboToCart` (mỗi thành phần 1 dòng riêng, chung `comboGroupId`,
 * giá từ `resolveComboLinePrices`, kiểm tồn đã chạy lúc tick trong popup).
 * CỐ Ý KHÔNG đi qua `mergeQuickAddIntoCart` — đường dòng phẳng của hàm đó sẽ
 * làm mất nhóm combo. `quantity` N = N cụm riêng, cùng quy ước "thêm combo N
 * lần" ở gợi ý tìm kiếm (`addComboToCart` luôn tạo cụm mới, có chủ đích).
 *
 * Combo nào dựng lỗi (thiếu chi tiết thành phần, không tính được giá phân bổ)
 * thì bỏ cụm đó và ghi vào `failedCombos` để caller báo toast — không chặn các
 * combo khác. Bắt lỗi TẠI ĐÂY (thay để ném lên updater) cùng khuôn `addCombo`
 * ở POS/index.tsx: message/toast chỉ được gọi ở nhánh catch, không ở render phase.
 */
export function appendComboPicksToCart(
  items: OrderItem[],
  picks: QuickAddComboPick[],
  fulfillmentMode: FulfillmentMode,
  newId: () => string = uuidv4,
): { items: OrderItem[]; addedCount: number; failedCombos: string[] } {
  let next = items;
  let addedCount = 0;
  const failedCombos: string[] = [];
  for (const pick of picks) {
    for (let i = 0; i < pick.quantity; i += 1) {
      try {
        next = addComboToCart(next, pick.combo, pick.details, fulfillmentMode, newId);
        addedCount += 1;
      } catch (error) {
        failedCombos.push(
          error instanceof Error ? error.message : `Không thêm được combo: ${pick.combo.combo_name}`,
        );
        break; // cụm này hỏng thì bỏ qua phần còn lại của cùng combo đó
      }
    }
  }
  return { items: next, addedCount, failedCombos };
}

/**
 * Dòng mặc định khi CHƯA có kết quả gợi ý giá (đang chờ API / lỗi): dựng bằng đúng
 * `variantToOrderItem` + `recalcItem` như `addItem`. Popup dùng làm giá trị tạm trước khi
 * `applyB2BSuggestToOrderItems` trả về.
 */
export function buildQuickAddBaseItem(
  variant: PosVariant,
  quantity: number,
  fulfillmentMode: FulfillmentMode,
  newId: () => string = uuidv4,
): OrderItem {
  return recalcItem({ ...variantToOrderItem(variant, fulfillmentMode, newId), quantity }, fulfillmentMode);
}
